import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { messagingAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import {
  ChatCircle,
  PaperPlaneTilt,
  Plus,
  UsersThree,
  User,
  MagnifyingGlass,
  ArrowLeft,
  Circle
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const MessagesPage = () => {
  const { user } = useAuth();
  const messagesEndRef = useRef(null);
  const pollingRef = useRef(null);

  const [conversations, setConversations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedConvo, setSelectedConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // New conversation dialog
  const [isNewConvoOpen, setIsNewConvoOpen] = useState(false);
  const [convoMode, setConvoMode] = useState('individual'); // 'individual', 'group', 'new_group'
  const [selectedRecipient, setSelectedRecipient] = useState('');
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [groupMessage, setGroupMessage] = useState('');
  const [newGroupName, setNewGroupName] = useState('');
  const [selectedGroupMembers, setSelectedGroupMembers] = useState([]);

  // Mobile: show conversation list or messages
  const [mobileView, setMobileView] = useState('list');

  useEffect(() => {
    loadData();
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, []);

  // Poll for new messages
  useEffect(() => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(() => {
      loadConversations();
      if (selectedConvo) loadMessages(selectedConvo.id, true);
    }, 5000);
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [selectedConvo]);

  const loadData = async () => {
    try {
      const [convRes, contactsRes, groupsRes] = await Promise.all([
        messagingAPI.getConversations(),
        messagingAPI.getContacts(),
        messagingAPI.getGroups()
      ]);
      setConversations(convRes.data);
      setContacts(contactsRes.data);
      setGroups(groupsRes.data);
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadConversations = async () => {
    try {
      const res = await messagingAPI.getConversations();
      setConversations(res.data);
    } catch (error) { /* silent */ }
  };

  const loadMessages = async (conversationId, silent = false) => {
    try {
      const res = await messagingAPI.getMessages(conversationId);
      setMessages(res.data);
      if (!silent) scrollToBottom();
    } catch (error) {
      if (!silent) toast.error('Failed to load messages');
    }
  };

  const scrollToBottom = () => {
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const selectConversation = async (convo) => {
    setSelectedConvo(convo);
    setMobileView('chat');
    await loadMessages(convo.id);
    scrollToBottom();
    // Refresh conversation list to update unread counts
    loadConversations();
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConvo) return;

    setSending(true);
    try {
      await messagingAPI.sendMessage({
        conversation_id: selectedConvo.id,
        content: newMessage.trim()
      });
      setNewMessage('');
      await loadMessages(selectedConvo.id);
      scrollToBottom();
      loadConversations();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleStartConversation = async () => {
    if (convoMode === 'individual') {
      if (!selectedRecipient) { toast.error('Please select a recipient'); return; }
      try {
        const res = await messagingAPI.createConversation({
          participant_ids: [selectedRecipient],
          is_group: false
        });
        setIsNewConvoOpen(false);
        await loadConversations();
        setSelectedConvo(res.data);
        setMobileView('chat');
        await loadMessages(res.data.id);
        setSelectedRecipient('');
      } catch (error) {
        toast.error('Failed to create conversation');
      }
    } else if (convoMode === 'group') {
      if (!selectedGroupId || !groupMessage.trim()) { toast.error('Select a group and write a message'); return; }
      try {
        const res = await messagingAPI.sendToGroup(selectedGroupId, groupMessage.trim());
        setIsNewConvoOpen(false);
        setGroupMessage('');
        setSelectedGroupId('');
        await loadConversations();
        // Select the new/existing conversation
        const convRes = await messagingAPI.getConversations();
        setConversations(convRes.data);
        const conv = convRes.data.find(c => c.id === res.data.conversation_id);
        if (conv) {
          setSelectedConvo(conv);
          setMobileView('chat');
          await loadMessages(conv.id);
        }
        toast.success('Message sent to group');
      } catch (error) {
        toast.error('Failed to send group message');
      }
    } else if (convoMode === 'new_group') {
      if (!newGroupName.trim() || selectedGroupMembers.length === 0) {
        toast.error('Enter group name and select members');
        return;
      }
      try {
        await messagingAPI.createGroup({
          name: newGroupName.trim(),
          member_ids: selectedGroupMembers
        });
        toast.success('Group created');
        setNewGroupName('');
        setSelectedGroupMembers([]);
        setConvoMode('group');
        const groupsRes = await messagingAPI.getGroups();
        setGroups(groupsRes.data);
      } catch (error) {
        toast.error('Failed to create group');
      }
    }
  };

  const toggleGroupMember = (memberId) => {
    setSelectedGroupMembers(prev =>
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';

  const getRoleColor = (role) => {
    switch (role) {
      case 'supervisor': return 'bg-primary text-primary-foreground';
      case 'caregiver': return 'bg-blue-600 text-white';
      case 'client': return 'bg-amber-600 text-white';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getConvoDisplayName = (convo) => {
    if (convo.is_group && convo.name) return convo.name;
    if (convo.participants?.length > 0) return convo.participants.map(p => p.name).join(', ');
    return 'Conversation';
  };

  const filteredConversations = conversations.filter(c => {
    if (!searchTerm) return true;
    const name = getConvoDisplayName(c).toLowerCase();
    return name.includes(searchTerm.toLowerCase());
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="fade-in" data-testid="messages-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground">Messages</h1>
          <p className="text-muted-foreground mt-1">Communicate with your team and clients</p>
        </div>

        <Dialog open={isNewConvoOpen} onOpenChange={setIsNewConvoOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-xl gap-2" data-testid="new-message-btn">
              <Plus weight="bold" className="w-4 h-4" />
              New Message
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">New Message</DialogTitle>
              <DialogDescription>Send a message to a person or group</DialogDescription>
            </DialogHeader>

            <div className="flex gap-2 mt-4">
              <Button variant={convoMode === 'individual' ? 'default' : 'outline'} size="sm" className="rounded-xl" onClick={() => setConvoMode('individual')} data-testid="mode-individual">
                <User weight="duotone" className="w-4 h-4 mr-1" /> Individual
              </Button>
              <Button variant={convoMode === 'group' ? 'default' : 'outline'} size="sm" className="rounded-xl" onClick={() => setConvoMode('group')} data-testid="mode-group">
                <UsersThree weight="duotone" className="w-4 h-4 mr-1" /> Group
              </Button>
              {user?.role === 'supervisor' && (
                <Button variant={convoMode === 'new_group' ? 'default' : 'outline'} size="sm" className="rounded-xl" onClick={() => setConvoMode('new_group')} data-testid="mode-create-group">
                  <Plus weight="bold" className="w-4 h-4 mr-1" /> Create Group
                </Button>
              )}
            </div>

            <div className="space-y-4 mt-4">
              {convoMode === 'individual' && (
                <>
                  <div className="space-y-2">
                    <Label>Select Recipient</Label>
                    <Select value={selectedRecipient} onValueChange={setSelectedRecipient}>
                      <SelectTrigger className="rounded-xl" data-testid="recipient-select">
                        <SelectValue placeholder="Choose a person" />
                      </SelectTrigger>
                      <SelectContent>
                        {contacts.map(c => (
                          <SelectItem key={c.id} value={c.id}>
                            <span className="flex items-center gap-2">
                              {c.name}
                              <span className="text-xs text-muted-foreground capitalize">({c.role})</span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button className="w-full rounded-xl" onClick={handleStartConversation} data-testid="start-conversation-btn">
                    Start Conversation
                  </Button>
                </>
              )}

              {convoMode === 'group' && (
                <>
                  <div className="space-y-2">
                    <Label>Select Group</Label>
                    <Select value={selectedGroupId} onValueChange={setSelectedGroupId}>
                      <SelectTrigger className="rounded-xl" data-testid="group-select">
                        <SelectValue placeholder="Choose a group" />
                      </SelectTrigger>
                      <SelectContent>
                        {groups.map(g => (
                          <SelectItem key={g.id} value={g.id}>
                            <span className="flex items-center gap-2">
                              {g.name}
                              <span className="text-xs text-muted-foreground">({g.member_count} members)</span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Message</Label>
                    <Textarea
                      value={groupMessage}
                      onChange={(e) => setGroupMessage(e.target.value)}
                      placeholder="Type your message to the group..."
                      rows={3}
                      className="rounded-xl"
                      data-testid="group-message-input"
                    />
                  </div>
                  <Button className="w-full rounded-xl" onClick={handleStartConversation} data-testid="send-group-message-btn">
                    <PaperPlaneTilt weight="bold" className="w-4 h-4 mr-2" />
                    Send to Group
                  </Button>
                </>
              )}

              {convoMode === 'new_group' && (
                <>
                  <div className="space-y-2">
                    <Label>Group Name</Label>
                    <Input
                      value={newGroupName}
                      onChange={(e) => setNewGroupName(e.target.value)}
                      placeholder="e.g., Team A Caregivers"
                      className="rounded-xl"
                      data-testid="new-group-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Select Members ({selectedGroupMembers.length} selected)</Label>
                    <ScrollArea className="h-48 rounded-xl border border-border p-2">
                      {contacts.map(c => (
                        <div
                          key={c.id}
                          className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 cursor-pointer"
                          onClick={() => toggleGroupMember(c.id)}
                          data-testid={`group-member-${c.id}`}
                        >
                          <Checkbox checked={selectedGroupMembers.includes(c.id)} />
                          <Avatar className="w-8 h-8">
                            <AvatarFallback className={`text-xs ${getRoleColor(c.role)}`}>
                              {getInitials(c.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{c.name}</p>
                            <p className="text-xs text-muted-foreground capitalize">{c.role}</p>
                          </div>
                        </div>
                      ))}
                    </ScrollArea>
                  </div>
                  <Button className="w-full rounded-xl" onClick={handleStartConversation} data-testid="create-group-btn">
                    Create Group
                  </Button>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Main Chat Layout */}
      <Card className="rounded-2xl border border-border overflow-hidden" data-testid="chat-container">
        <div className="flex h-[calc(100vh-16rem)] min-h-[500px]">
          {/* Conversation List */}
          <div className={`w-full md:w-80 lg:w-96 border-r border-border flex flex-col ${mobileView === 'chat' ? 'hidden md:flex' : 'flex'}`}>
            <div className="p-4 border-b border-border">
              <div className="relative">
                <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search conversations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 rounded-xl text-sm"
                  data-testid="search-conversations-input"
                />
              </div>
            </div>
            <ScrollArea className="flex-1">
              {filteredConversations.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 px-4">
                  <ChatCircle weight="duotone" className="w-12 h-12 text-muted-foreground/50 mb-3" />
                  <p className="text-sm text-muted-foreground text-center">
                    {searchTerm ? 'No conversations found' : 'No conversations yet'}
                  </p>
                  <p className="text-xs text-muted-foreground text-center mt-1">
                    Click "New Message" to start one
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {filteredConversations.map(convo => (
                    <div
                      key={convo.id}
                      className={`flex items-center gap-3 p-4 cursor-pointer transition-colors hover:bg-muted/50 ${selectedConvo?.id === convo.id ? 'bg-muted/70' : ''}`}
                      onClick={() => selectConversation(convo)}
                      data-testid={`conversation-${convo.id}`}
                    >
                      <div className="relative shrink-0">
                        <Avatar className="w-10 h-10">
                          <AvatarFallback className={`text-xs ${convo.is_group ? 'bg-secondary text-secondary-foreground' : getRoleColor(convo.participants?.[0]?.role)}`}>
                            {convo.is_group ? (
                              <UsersThree weight="duotone" className="w-5 h-5" />
                            ) : (
                              getInitials(convo.participants?.[0]?.name)
                            )}
                          </AvatarFallback>
                        </Avatar>
                        {convo.unread_count > 0 && (
                          <span className="absolute -top-1 -right-1 w-5 h-5 bg-destructive text-destructive-foreground text-xs font-bold rounded-full flex items-center justify-center">
                            {convo.unread_count}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className={`text-sm truncate ${convo.unread_count > 0 ? 'font-semibold' : 'font-medium'}`}>
                            {getConvoDisplayName(convo)}
                          </p>
                          {convo.last_message_at && (
                            <span className="text-xs text-muted-foreground shrink-0 ml-2">
                              {format(new Date(convo.last_message_at), 'MMM d')}
                            </span>
                          )}
                        </div>
                        {convo.last_message && (
                          <p className="text-xs text-muted-foreground truncate mt-0.5">
                            {convo.last_message}
                          </p>
                        )}
                        {convo.is_group && (
                          <Badge variant="secondary" className="text-[10px] rounded-full mt-1 px-1.5 py-0">
                            Group
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Message Area */}
          <div className={`flex-1 flex flex-col ${mobileView === 'list' ? 'hidden md:flex' : 'flex'}`}>
            {!selectedConvo ? (
              <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
                <ChatCircle weight="duotone" className="w-16 h-16 mb-4 opacity-40" />
                <p className="font-heading text-lg">Select a conversation</p>
                <p className="text-sm mt-1">Or start a new one</p>
              </div>
            ) : (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b border-border flex items-center gap-3 bg-card">
                  <Button variant="ghost" size="sm" className="md:hidden rounded-xl" onClick={() => { setMobileView('list'); setSelectedConvo(null); }} data-testid="back-to-convos-btn">
                    <ArrowLeft weight="bold" className="w-4 h-4" />
                  </Button>
                  <Avatar className="w-9 h-9">
                    <AvatarFallback className={`text-xs ${selectedConvo.is_group ? 'bg-secondary text-secondary-foreground' : getRoleColor(selectedConvo.participants?.[0]?.role)}`}>
                      {selectedConvo.is_group ? (
                        <UsersThree weight="duotone" className="w-5 h-5" />
                      ) : (
                        getInitials(selectedConvo.participants?.[0]?.name)
                      )}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-medium text-sm">{getConvoDisplayName(selectedConvo)}</p>
                    <p className="text-xs text-muted-foreground">
                      {selectedConvo.is_group
                        ? `${selectedConvo.participant_ids?.length || 0} members`
                        : selectedConvo.participants?.[0]?.role
                      }
                    </p>
                  </div>
                </div>

                {/* Messages */}
                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-4">
                    {messages.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <p className="text-sm">No messages yet. Say hello!</p>
                      </div>
                    ) : (
                      messages.map(msg => {
                        const isMe = msg.sender_id === user?.id;
                        return (
                          <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`} data-testid={`message-${msg.id}`}>
                            <div className={`max-w-[75%] ${isMe ? 'items-end' : 'items-start'} flex flex-col`}>
                              {!isMe && (
                                <span className="text-xs text-muted-foreground mb-1 ml-1">
                                  {msg.sender_name}
                                  <span className="capitalize"> &middot; {msg.sender_role}</span>
                                </span>
                              )}
                              <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${isMe ? 'bg-primary text-primary-foreground rounded-br-md' : 'bg-muted rounded-bl-md'}`}>
                                {msg.content}
                              </div>
                              <span className="text-[10px] text-muted-foreground mt-1 mx-1">
                                {format(new Date(msg.created_at), 'h:mm a')}
                              </span>
                            </div>
                          </div>
                        );
                      })
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>

                {/* Message Input */}
                <form onSubmit={handleSendMessage} className="p-4 border-t border-border bg-card">
                  <div className="flex items-center gap-3">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type a message..."
                      className="rounded-xl flex-1"
                      disabled={sending}
                      data-testid="message-input"
                    />
                    <Button
                      type="submit"
                      disabled={!newMessage.trim() || sending}
                      className="rounded-xl shrink-0"
                      data-testid="send-message-btn"
                    >
                      <PaperPlaneTilt weight="bold" className="w-4 h-4" />
                    </Button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default MessagesPage;
